// @givmo/compliance — what the Calendar lists (CCM-R1-INTAKE (a); frames 07, 09; slice 1's design).
// Pure functions over the Filings composition; no React, no reads.
//
// ══ THREE SECTIONS, NEVER SHARING A WORD ═══════════════════════════════════════════════════════
//
// "Needs your input" is a date only the organization has: a question, with a way to answer it.
// "Coming up" is a date and what it is counted from, with a quiet line or two (the send waiting for
// its confirmation, the extension, an own filing's library line). "Not supported yet" is Givmo's
// gap, stated as Givmo's. Missing information and unsupported coverage never share a section or a
// word (slice 1's design, "Not supported vs missing").
//
// ⚠ A FILING WITH NO DATE IS LISTED, NOT DROPPED. A calendar that quietly left out a filing it cannot
// date would let a missing date read as "nothing is due". Each undated filing lands in the section
// that says which kind of "no date" it is. Only a filing that is done (filed once) or no longer
// tracked leaves the Calendar; Filings still lists it.
//
// ⚠ ROW 7'S SENTENCE goes at the Calendar's foot only while it lists a library filing's worked-out
// date or its basis (plan §3 row 7; A-X1). An own filing's dates are the organization's, and a
// question or a gap works nothing out, so a Calendar of only those carries none.

import { displayComplianceDate } from './compliance-decode';
import {
  firstPeriodWaitsQuestion,
  fiscalYearEndedLine,
  nothingRecordedLine,
} from './compliance-panel-model';
import type { FilingsRecords, FollowThroughIndex } from './compliance-filings-reads';
import { filingKey, type FilingRef, type FilingRow } from './compliance-filings-model';
import {
  followThroughLine,
  followThroughSentence,
  isException,
  lastSendFollowThrough,
  type FollowThroughLine,
} from './compliance-results-model';
import type { CaseFollowThrough } from './compliance-submission-decode';
import {
  cycleBasisLine,
  DATE_PASSED,
  extensionLine,
  libraryBasisLine,
  NO_DATE_YET_LINE,
  ownBasisLine,
  recordedFiscalYearEnd,
} from './compliance-panel-model';

/** An own filing's line on the Calendar (frame 09). */
export const OWN_FILING_CALENDAR_LINE = 'Givmo’s library doesn’t have this filing.';

/** What "Not supported yet" says of every filing in it (copy ruling item 4): D-CCM-71's L1. */
export const NOT_SUPPORTED_REASON = NO_DATE_YET_LINE;

/** A date only the organization has, asked for. */
export interface CalendarQuestion {
  key: string;
  ref: FilingRef;
  title: string;
  question: string;
  /**
   * A question no date on this page answers, so its button opens the filing ("Open the filing")
   * rather than offering a date: a first year waiting on another filing's sending (C1-c).
   */
  opens?: true;
}

/** A date coming up, or a filing due before first soliciting. */
export interface CalendarDate {
  key: string;
  ref: FilingRef;
  title: string;
  /** The served date; absent for a filing due before first soliciting. */
  dueOn?: string;
  /** What the date column says: the date, or nothing for a filing with none. */
  dateLabel: string;
  /** MDEXT-OPEN: N1 under the date, while the year is held open by its automatic extension. */
  datePassed?: string;
  /** What the date is counted from. */
  basis?: string;
  /**
   * The day a check found a possible change to a source of the rule the date is counted from, while
   * that change is under review (CCM-OPS1-WEB): read off the provenance the filing's panel shows, so
   * the two never disagree. The row words it after its basis (Q3's short form; ruling 6).
   */
  sourceChangeFoundOn?: string;
  /**
   * What came back after the send the row speaks of, where it is an exception (CCM-R1-RESULTS-WEB;
   * D-CCM-68, section 6): said at body weight above the quiet lines, so it never passes for routine.
   */
  exception?: string;
  /** The quiet lines under it, in order. */
  lines: string[];
  /** The organization's own filing: its dates are its own, worked out from no law. */
  own: boolean;
}

/** A filing Givmo cannot date yet. */
export interface CalendarGap {
  key: string;
  ref: FilingRef;
  title: string;
  reason: string;
  /** Its last recorded act, where there is one. */
  last?: string;
  /** Whether that line says an exception came back (body weight, as a date row's does). */
  lastException?: boolean;
}

export interface CalendarView {
  /** CCM-R1-LATE-FILINGS (D-CCM-62, frame 1d): the years read overdue, oldest first, at the top. */
  overdue: CalendarDate[];
  questions: CalendarQuestion[];
  dates: CalendarDate[];
  gaps: CalendarGap[];
  /** Whether row 7's sentence goes at the foot: a library filing's worked-out date or basis shows. */
  sentence: boolean;
  /** Nothing recorded at all: the first visit, in place of the Calendar. */
  firstVisit: boolean;
}

/** "Maryland · Annual report", as every Compliance screen names a filing. */
export function calendarTitle(row: FilingRow): string {
  return row.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : row.headline;
}

/** The send still waiting: "Sent Sep 10, 2026, waiting for Virginia’s confirmation." */
function waitingLine(on: string, from: string | undefined, lead: string): string | undefined {
  if (from === undefined || from.length === 0) return undefined;
  return `${lead} ${displayComplianceDate(on)}, waiting for ${from}’s confirmation.`;
}

/** A filing's last recorded act, as the Calendar says it under a date or a gap (frames 07, 09). */
function lastActLine(row: FilingRow): string | undefined {
  const last = row.lastAct;
  if (last === undefined) return undefined;
  if (last.kind === 'filed')
    return `Last filed ${displayComplianceDate(last.on)}, confirmation on file.`;
  // Copy ruling item 1: "Last report sent" for a report, "Last filing sent" for every other.
  return waitingLine(
    last.on,
    row.confirmationFrom,
    row.isReport ? 'Last report sent' : 'Last filing sent',
  );
}

/** A line saying what came back after a send, and whether it is an exception. */
interface FollowedLine {
  line: string;
  exception: boolean;
}

/**
 * A line saying what came back, in the Calendar's sentence form (D-CCM-68, section 6; a past year's
 * leads with "Last report sent"), or `undefined` to keep the register's line: nothing known, or
 * the confirmation on file.
 */
function said(
  line: FollowThroughLine | undefined,
  jurisdiction: string,
  pastLead?: string,
): FollowedLine | undefined {
  return line !== undefined
    ? { line: followThroughSentence(line, jurisdiction, pastLead), exception: isException(line) }
    : undefined;
}

/** What came back after one year's latest send, as "Sent …" says it. */
function followedLine(
  send: CaseFollowThrough | undefined,
  jurisdiction: string,
  nameOf: (userId: string | undefined) => string | undefined,
): FollowedLine | undefined {
  return said(followThroughLine(send, nameOf), jurisdiction);
}

/**
 * The filing's latest send of any year, said for its last recorded act in the row's own lead: where
 * it isn't the year the row already speaks for (`lastSendFollowThrough`'s rule otherwise).
 */
function lastSendLine(
  row: FilingRow,
  jurisdiction: string,
  nameOf: (userId: string | undefined) => string | undefined,
  thisYearCycleId?: string,
): FollowedLine | undefined {
  const send = row.latestSend;
  if (thisYearCycleId !== undefined && send?.renewalCycleId === thisYearCycleId) return undefined;
  return said(
    lastSendFollowThrough(row.lastAct?.on, send, nameOf),
    jurisdiction,
    row.isReport ? 'Last report sent' : 'Last filing sent',
  );
}

/** Ascending by date, then by name; a filing with no date after every dated one. */
function byDate(a: CalendarDate, b: CalendarDate): number {
  if (a.dueOn !== undefined && b.dueOn !== undefined && a.dueOn !== b.dueOn) {
    return a.dueOn.localeCompare(b.dueOn);
  }
  if (a.dueOn === undefined && b.dueOn !== undefined) return 1;
  if (a.dueOn !== undefined && b.dueOn === undefined) return -1;
  return a.title.localeCompare(b.title);
}

const byTitle = <T extends { title: string }>(a: T, b: T) => a.title.localeCompare(b.title);

/**
 * The Calendar, from the rows Filings lists. Each row lands in exactly one section, or none when it is
 * done or no longer tracked. A year's line says what came back after its latest send where the
 * organization-wide read knows (`followThrough`, CCM-R1-RESULTS-WEB), naming who recorded an answer
 * by `nameOf`; without it, the register's.
 */
export function composeCalendar(
  records: FilingsRecords,
  rows: readonly FilingRow[],
  followThrough?: FollowThroughIndex,
  nameOf: (userId: string | undefined) => string | undefined = () => undefined,
): CalendarView {
  const recordedFye = recordedFiscalYearEnd(records.profile);
  const overdue: CalendarDate[] = [];
  const questions: CalendarQuestion[] = [];
  const dates: CalendarDate[] = [];
  const gaps: CalendarGap[] = [];

  for (const row of rows) {
    const key = filingKey(row.ref);
    const title = calendarTitle(row);
    const next = row.next;
    const jurisdiction = row.confirmationFrom ?? row.headline;
    // Each year read overdue (`overdueCycles`), beside the row's own next date.
    for (const cycle of row.overdue ?? []) {
      if (cycle.dueOn === undefined) continue;
      // With Y6 after it on a short year, as on the panel's Overdue card (cut 1008-42's review, F9).
      const yearLine = fiscalYearEndedLine(cycle);
      overdue.push({
        key: `${key}-overdue-${cycle.cycleId}`,
        ref: row.ref,
        title,
        dueOn: cycle.dueOn,
        dateLabel: displayComplianceDate(cycle.dueOn),
        lines: [...(yearLine !== undefined ? [yearLine] : []), nothingRecordedLine(cycle)],
        own: false,
      });
    }
    switch (next.kind) {
      case 'needs-date':
      case 'needs-fiscal-year-end':
        // Copy ruling item 3: per-rule wording is not served (CCM-R1-FILING-META-2).
        questions.push({
          key,
          ref: row.ref,
          title,
          question: `Givmo needs one date from you to work out ${jurisdiction}’s next date.`,
        });
        break;
      case 'dated': {
        if (next.own) {
          const filing = records.ownFilings.find((candidate) => candidate.filingId === row.ref.id);
          const open = filing?.latestCycle;
          // Recording a period opens the next, so a send still waiting is usually an earlier one's.
          const status =
            open?.submissionRecordedOn !== undefined && !open.filed
              ? waitingLine(open.submissionRecordedOn, row.confirmationFrom, 'Sent')
              : row.lastAct?.kind === 'sent'
                ? lastActLine(row)
                : undefined;
          dates.push({
            key,
            ref: row.ref,
            title,
            dueOn: next.dueOn,
            dateLabel: displayComplianceDate(next.dueOn),
            basis: ownBasisLine(filing?.cadence, filing?.cadenceInterval),
            lines: [...(status !== undefined ? [status] : []), OWN_FILING_CALENDAR_LINE],
            own: true,
          });
          break;
        }
        const obligation = records.obligations.find(
          (candidate) => candidate.obligationId === row.ref.id,
        );
        const cycle = next.cycle;
        const sentOn = cycle?.submissionRecordedOn;
        // What came back after this year's latest send, else after the last year's (D-CCM-68).
        const thisYear = followedLine(
          followThrough?.forYear(row.ref.id, cycle?.cycleId),
          jurisdiction,
          nameOf,
        );
        const followed =
          thisYear ??
          (sentOn === undefined
            ? lastSendLine(row, jurisdiction, nameOf, cycle?.cycleId)
            : undefined);
        // This year sent and waiting (frame 09's Virginia), else the last year still waiting
        // (frame 07's Maryland): never both, and never a filed year.
        const status =
          followed !== undefined
            ? followed.exception
              ? undefined
              : followed.line
            : sentOn !== undefined
              ? waitingLine(sentOn, row.confirmationFrom, 'Sent')
              : row.lastAct?.kind === 'sent'
                ? lastActLine(row)
                : undefined;
        const extension = extensionLine(cycle?.extension ?? next.occurrence?.extension);
        // The panel's date block shows the year's own provenance, else the filing's.
        const changeFoundOn = (cycle?.provenance ?? obligation?.provenance)
          ?.ruleSourceChangeFoundOn;
        dates.push({
          key,
          ref: row.ref,
          title,
          dueOn: next.dueOn,
          dateLabel: displayComplianceDate(next.dueOn),
          ...(next.datePassed === true ? { datePassed: DATE_PASSED } : {}),
          basis:
            obligation !== undefined ? cycleBasisLine(obligation, cycle, recordedFye) : undefined,
          ...(changeFoundOn !== undefined ? { sourceChangeFoundOn: changeFoundOn } : {}),
          ...(followed?.exception === true ? { exception: followed.line } : {}),
          lines: [status, extension].filter((line): line is string => line !== undefined),
          own: false,
        });
        break;
      }
      case 'before-first-solicitation': {
        const changeFoundOn = records.obligations.find(
          (candidate) => candidate.obligationId === row.ref.id,
        )?.provenance.ruleSourceChangeFoundOn;
        // A filing made once: its send's line, in the row's own "Last filing sent" voice.
        const followedOnce = lastSendLine(row, jurisdiction, nameOf);
        // No date to put in the date column: the rule's own timing goes where a basis goes, in the
        // approved words (copy ruling item 2), as the panel leads with it.
        dates.push({
          key,
          ref: row.ref,
          title,
          dateLabel: '',
          basis: libraryBasisLine({
            dueDateBasis: 'before_first_solicitation',
            sourceDeadline: {},
            jurisdictionName: jurisdiction,
          }),
          ...(changeFoundOn !== undefined ? { sourceChangeFoundOn: changeFoundOn } : {}),
          ...(followedOnce?.exception === true ? { exception: followedOnce.line } : {}),
          lines:
            followedOnce !== undefined
              ? followedOnce.exception
                ? []
                : [followedOnce.line]
              : row.lastAct?.kind === 'sent'
                ? [lastActLine(row)].filter((l): l is string => !!l)
                : [],
          own: false,
        });
        break;
      }
      case 'not-supported': {
        const followedLast = lastSendLine(row, jurisdiction, nameOf);
        const last = followedLast?.line ?? lastActLine(row);
        gaps.push({
          key,
          ref: row.ref,
          title,
          reason: NOT_SUPPORTED_REASON,
          ...(last !== undefined ? { last } : {}),
          ...(followedLast?.exception === true ? { lastException: true } : {}),
        });
        break;
      }
      case 'first-period-waits':
        // D-CCM-110 J-F: no year is dated until the registration statement's sending is recorded
        // (C1-c, D-CCM-112), so the question opens the filing, where it can be recorded.
        questions.push({
          key,
          ref: row.ref,
          title,
          question: firstPeriodWaitsQuestion(jurisdiction),
          opens: true,
        });
        break;
      case 'filed-once':
      case 'no-longer-tracked':
        break;
    }
  }

  return {
    overdue: overdue.sort(byDate),
    questions: questions.sort(byTitle),
    dates: dates.sort(byDate),
    gaps: gaps.sort(byTitle),
    sentence: dates.some((date) => !date.own) || overdue.length > 0,
    firstVisit: rows.length === 0,
  };
}
